// src/oura/stageLabels.ts
//
// One name for each pipeline stage, in one place (#3535).
//
// The labels used to live privately inside the progress toast, with a comment
// claiming they mirrored a second copy in Businesses.tsx. That copy did not
// exist, so "mirrors" described a relationship nobody could check — and the one
// real copy silently fell behind the backend.

import type { AeoAssessment } from "@service/aeo";

/**
 * Stage name → what an operator should read.
 *
 * Keyed by the backend's stage NAME, never by position. `AEO_STAGES` in
 * `app/data/models/aeo.py` has grown twice now (ourteam-backend#4598 inserted
 * `research` before `queries` and `verify` after `analyze`), and a positional
 * lookup renames every stage after the insertion point without anything
 * failing — the count stays right while each label points one stage behind.
 *
 * The order below matches the backend's for readability only. Nothing reads it
 * as a sequence, so a stage inserted in the middle costs one line here and
 * breaks nothing in the meantime.
 */
const STAGE_LABELS: Record<string, string> = {
    preflight: "Checking the site",
    workspace: "Preparing workspace",
    init: "Starting the engine",
    competitors: "Finding competitors",
    research: "Researching the business",
    queries: "Generating questions",
    run_engines: "Probing AI engines",
    analyze: "Analyzing results",
    verify: "Verifying mentions",
    exports: "Preparing exports",
    pagespeed: "Checking page speed",
    audit_fallback: "Auditing the site",
    page_drafts: "Drafting page fixes",
    copywriting: "Writing the report",
    render_pdf: "Rendering the report",
    finalize: "Finishing up",
};

/** `run_engines` -> `Run Engines`. Readable, and honest that we do not know it. */
function humanizeStage(raw: string): string {
    return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * What to show for a stage.
 *
 * An unknown stage title-cases rather than falling back to a neighbour's label
 * or an empty string: a backend that ships a new stage on Monday should read
 * slightly rough here, not wrong. That is what keeps "adding a stage changes no
 * FE code" true between the deploy and someone writing the nicer wording.
 */
export function stageLabel(stage: string | null | undefined): string {
    if (!stage) return "";
    return STAGE_LABELS[stage] ?? humanizeStage(stage);
}

/** Every stage the FE has real wording for — exported for tests, not for ordering. */
export const KNOWN_STAGES = Object.keys(STAGE_LABELS);

/** Convenience for the two progress surfaces, which both hold an assessment. */
export function assessmentStageLabel(a: Pick<AeoAssessment, "stage"> | null | undefined): string {
    return stageLabel(a?.stage);
}
